/**
 * VhalGrpcProvider
 *
 * Frontend half of the VHAL gRPC pass-through. Sits alongside the legacy
 * CuttlefishVhalProvider (which routes via ADB) and gives consumers a
 * factory for typed Client<typeof VehicleServer> instances that talk to
 * ctrl's gRPC-Web endpoint. ctrl re-exports the AOSP VehicleServer
 * service unchanged.
 *
 * The provider holds the ambient connection config (baseUrl + token
 * source); per-emulator clients are produced on demand by
 * useVhalGrpcClient(emulatorId). Mount once near the top of the app.
 */

import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from "react";
import type { Client } from "@connectrpc/connect";
import { createVhalClient, type VehicleServer } from "@repo/sdk/vhal";

interface VhalGrpcContextValue {
  baseUrl: string;
  getToken: () => string | Promise<string>;
  getGitlabBase?: () => string | undefined;
}

const VhalGrpcContext = createContext<VhalGrpcContextValue | null>(null);

export interface VhalGrpcProviderProps {
  /**
   * Base URL of ctrl's gRPC-Web listener. Typically derived from the
   * portal's NA_CTRL_GRPC_URL env var, falling back to <NA_CTRL_API_URL>/grpc
   * when not set.
   */
  baseUrl: string;
  /**
   * Returns a fresh OIDC bearer on each call (interceptor invokes per RPC).
   * Wire this to the OIDC user manager's getAccessToken() so refreshed
   * tokens propagate without re-mounting the provider.
   */
  getToken: () => string | Promise<string>;
  /**
   * GitLab instance URL for ctrl's PAT authenticator (Null-Gitlab-Base
   * header). Omit when authenticating with OIDC (e.g. the portal).
   */
  getGitlabBase?: () => string | undefined;
  children: ReactNode;
}

export const VhalGrpcProvider: FC<VhalGrpcProviderProps> = ({
  baseUrl,
  getToken,
  getGitlabBase,
  children,
}) => {
  const value = useMemo<VhalGrpcContextValue>(
    () => ({ baseUrl, getToken, getGitlabBase }),
    [baseUrl, getToken, getGitlabBase],
  );

  return (
    <VhalGrpcContext.Provider value={value}>
      {children}
    </VhalGrpcContext.Provider>
  );
};

/**
 * useVhalGrpc returns the ambient connection config. Throws if no provider
 * is mounted.
 */
export function useVhalGrpc(): VhalGrpcContextValue {
  const v = useContext(VhalGrpcContext);
  if (!v) {
    throw new Error(
      "useVhalGrpc() called outside <VhalGrpcProvider>. Mount the provider " +
        "near the top of the app before invoking VHAL gRPC hooks.",
    );
  }
  return v;
}

/**
 * useVhalGrpcClient returns a typed VehicleServer client for the given
 * emulator. Memoized on emulatorId so changing emulators rebuilds the
 * client (cheap — no I/O until first RPC).
 */
export function useVhalGrpcClient(
  emulatorId: string,
): Client<typeof VehicleServer> {
  const { baseUrl, getToken, getGitlabBase } = useVhalGrpc();
  return useMemo(
    () => createVhalClient({ baseUrl, emulatorId, getToken, getGitlabBase }),
    [baseUrl, emulatorId, getToken, getGitlabBase],
  );
}
